<template>
  <div class="beta-users">
    <div class="beta-users-header">
      <h1>Beta User Management</h1>
      <div class="header-actions">
        <v-btn
          data-test="export-button"
          color="secondary"
          @click="exportUsers"
          :disabled="loading || betaUsers.length === 0"
        >
          <v-icon left>mdi-download</v-icon>
          Export CSV
        </v-btn>
        <v-btn
          data-test="refresh-button"
          color="primary"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Filters -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchQuery"
          data-test="search-input"
          label="Search by email or name"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="statusFilter"
          data-test="status-filter"
          :items="statusOptions"
          label="Filter by status"
          clearable
          outlined
          dense
        />
      </v-col>
    </v-row>

    <!-- Loading State -->
    <div v-if="loading" data-test="loading-spinner" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>

    <!-- Error State -->
    <v-alert
      v-if="error"
      data-test="error-alert"
      type="error"
      dismissible
      @click:close="error = null"
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <!-- Empty State -->
    <div
      v-if="!loading && filteredUsers.length === 0"
      data-test="empty-state"
      class="empty-state text-center py-8"
    >
      <v-icon size="64" color="grey">mdi-account-group-outline</v-icon>
      <p class="text-h6 mt-4">No beta users found</p>
      <p class="text-body-2 grey--text">
        {{ searchQuery || statusFilter ? 'Try adjusting your filters' : 'Beta users will appear here when they sign up' }}
      </p>
    </div>

    <!-- Users Table -->
    <v-card v-if="!loading && filteredUsers.length > 0" data-test="beta-users-table">
      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :items-per-page="10"
        show-expand
        v-model:expanded="expanded"
      >
        <template v-slot:item="{ item, index }">
          <tr data-test="user-row">
            <td>
              <v-btn
                :data-test="`expand-button-${index}`"
                icon
                @click="toggleExpand(index)"
              >
                <v-icon>{{ expandedIndices.includes(index) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </td>
            <td data-test="user-email">{{ item.email }}</td>
            <td data-test="user-name">{{ item.name || '-' }}</td>
            <td data-test="user-role">{{ item.role }}</td>
            <td>{{ item.organization || '-' }}</td>
            <td data-test="user-device">{{ item.device_name || '-' }}</td>
            <td data-test="user-os">
              <v-chip v-if="item.operating_system" :color="item.operating_system === 'ios' ? 'blue' : 'green'" small>
                {{ item.operating_system.toUpperCase() }}
              </v-chip>
              <span v-else>-</span>
            </td>
            <td>
              <v-chip
                :color="getStatusColor(item.status)"
                small
                data-test="user-status"
              >
                {{ item.status }}
              </v-chip>
            </td>
            <td data-test="requested-date">{{ formatDate(item.requested_at) }}</td>
            <td>
              <div v-if="item.status === 'pending'" class="action-buttons">
                <v-btn
                  data-test="approve-button"
                  color="success"
                  small
                  @click="approveUser(item.id)"
                  :loading="approvingId === item.id"
                >
                  Approve
                </v-btn>
                <v-btn
                  data-test="reject-button"
                  color="error"
                  small
                  @click="showRejectDialog(item)"
                  :loading="rejectingId === item.id"
                  class="ml-2"
                >
                  Reject
                </v-btn>
              </div>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ item, index }">
          <tr v-if="expandedIndices.includes(index)">
            <td :colspan="headers.length + 1">
              <div :data-test="`expanded-content-${index}`" class="expanded-content pa-4">
                <div class="mb-4">
                  <h4>Basic Information</h4>
                  <div class="mt-2">
                    <strong>Phone:</strong> {{ item.phone || 'Not provided' }}
                  </div>
                  <div class="mt-2" v-if="item.reason">
                    <strong>Reason for joining:</strong> {{ item.reason }}
                  </div>
                  <div class="mt-2" v-if="item.referral_source">
                    <strong>Referral source:</strong> {{ item.referral_source }}
                  </div>
                </div>
                <div class="mb-4" v-if="item.device_name || item.operating_system">
                  <h4>Device Information</h4>
                  <div class="mt-2" v-if="item.device_name">
                    <strong>Device:</strong> {{ item.device_name }}
                  </div>
                  <div class="mt-2" v-if="item.operating_system">
                    <strong>Operating System:</strong> {{ item.operating_system.toUpperCase() }}
                  </div>
                </div>
                <div v-if="item.metadata && Object.keys(item.metadata).length > 0">
                  <h4>Additional Information</h4>
                  <div v-for="(value, key) in item.metadata" :key="key" class="mt-2">
                    <strong>{{ formatKey(key) }}:</strong>
                    <span v-if="Array.isArray(value)">{{ value.join(', ') }}</span>
                    <span v-else>{{ value }}</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="400"
      data-test="confirm-dialog"
    >
      <v-card>
        <v-card-title>Confirm Rejection</v-card-title>
        <v-card-text>
          Are you sure you want to reject this beta user request?
          <div class="mt-2" v-if="userToReject">
            <strong>Email:</strong> {{ userToReject.email }}<br>
            <strong>Name:</strong> {{ userToReject.name || 'N/A' }}<br>
            <strong>Device:</strong> {{ userToReject.device_name || 'Not specified' }}<br>
            <strong>OS:</strong> {{ userToReject.operating_system ? userToReject.operating_system.toUpperCase() : 'Not specified' }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="confirmReject"
            :loading="rejectingId !== null"
          >
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAdminStore } from '@/store/modules/admin'

const adminStore = useAdminStore()

// Reactive data
const searchQuery = ref('')
const statusFilter = ref('')
const expanded = ref([])
const expandedIndices = ref([])
const confirmDialog = ref(false)
const userToReject = ref(null)
const approvingId = ref(null)
const rejectingId = ref(null)

// Computed properties
const betaUsers = computed(() => adminStore.betaUsers)
const loading = computed(() => adminStore.loading)
const error = computed(() => adminStore.error)

const filteredUsers = computed(() => {
  let users = betaUsers.value

  // Apply status filter
  if (statusFilter.value) {
    users = users.filter(user => user.status === statusFilter.value)
  }

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    users = users.filter(user => 
      user.email.toLowerCase().includes(query) ||
      (user.name && user.name.toLowerCase().includes(query))
    )
  }

  return users
})

// Data
const statusOptions = [
  { text: 'All', value: '' },
  { text: 'Pending', value: 'pending' },
  { text: 'Approved', value: 'approved' },
  { text: 'Rejected', value: 'rejected' }
]

const headers = [
  { text: '', value: 'data-table-expand', sortable: false },
  { text: 'Email', value: 'email' },
  { text: 'Name', value: 'name' },
  { text: 'Role', value: 'role' },
  { text: 'Organization', value: 'organization' },
  { text: 'Device', value: 'device_name' },
  { text: 'OS', value: 'operating_system' },
  { text: 'Status', value: 'status' },
  { text: 'Requested', value: 'requested_at' },
  { text: 'Actions', value: 'actions', sortable: false }
]

// Methods
const refreshData = async () => {
  await adminStore.fetchBetaUsers()
}

const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error'
  }
  return colors[status] || 'grey'
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

const formatKey = (key) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const toggleExpand = (index) => {
  const idx = expandedIndices.value.indexOf(index)
  if (idx > -1) {
    expandedIndices.value.splice(idx, 1)
  } else {
    expandedIndices.value.push(index)
  }
}

const approveUser = async (userId) => {
  approvingId.value = userId
  try {
    await adminStore.approveBetaUser(userId)
  } finally {
    approvingId.value = null
  }
}

const showRejectDialog = (user) => {
  userToReject.value = user
  confirmDialog.value = true
}

const confirmReject = async () => {
  if (!userToReject.value) return
  
  rejectingId.value = userToReject.value.id
  try {
    await adminStore.rejectBetaUser(userToReject.value.id)
    confirmDialog.value = false
    userToReject.value = null
  } finally {
    rejectingId.value = null
  }
}

const exportUsers = async () => {
  await adminStore.exportBetaUsers()
}

// Lifecycle
onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.beta-users {
  padding: 24px;
}

.beta-users-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.action-buttons {
  display: flex;
  align-items: center;
}

.expanded-content {
  background-color: #f5f5f5;
}

.empty-state {
  color: #666;
}
</style>